/**
 * i18n 設定
 *
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business 多言語(JA/EN)リソース定義。ナビ・スクリーン分析・ファネル・ユーザー行動・エラー分析の翻訳キーを管理
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import {
  jaUserEventNames,
  jaUserPropNames,
  enUserEventNames,
  enUserPropNames,
} from './i18n-user-labels.js';

/** @graph-connects none */
const ja = {
  common: {
    noData: 'データがありません',
    loading: '読み込み中...',
    error: 'エラーが発生しました',
    comingSoon: 'Coming Soon',
    comingSoonDescription: 'この機能は現在準備中です',
    last7days: '7日間',
    last30days: '30日間',
    last90days: '90日間',
  },
  nav: {
    overview: 'オーバービュー',
    screens: 'スクリーン分析',
    funnels: 'ファネル分析',
    serviceHealth: 'サービスヘルス',
    users: 'ユーザー行動',
    errors: 'エラー分析',
    stylingOverview: 'オーバービュー',
    stylingTiming: 'タイミング分析',
    stylingErrors: 'エラー分析',
    stylingSkuAnalysis: 'SKU 分析',
    stylingFilterPatterns: 'フィルタ分析',
    stylingStylistActivity: 'スタイリスト行動',
  },
  screens: {
    title: 'スクリーン分析',
    description: '画面ごとのメトリクスとユーザー行動',
    search: 'スクリーン名で検索...',
    screenName: 'スクリーン',
    totalEvents: 'イベント数',
    uniqueUsers: 'ユニークユーザー',
    uniqueSessions: 'ユニークセッション',
    avgDwellTime: '平均滞在時間',
    viewDetail: '詳細を見る',
    backToList: 'スクリーン一覧に戻る',
    detailTitle: 'スクリーン詳細分析',
    metricsTab: 'メトリクス',
    eventsTab: 'イベント',
    dimensionsTab: 'ディメンション',
    dailyTrend: '日次トレンド',
    eventBreakdown: 'イベント内訳',
    dimensionBreakdown: 'ディメンション内訳',
    eventCount: 'イベント数',
  },
  overview: {
    title: 'オーバービュー',
    description: 'UXインサイトの全体概要',
    todayKpi: '本日の数値',
    todayUsers: 'ユニークユーザー',
    todaySessions: 'ユニークセッション',
    monthlyRegistrations: '月額会員登録',
    purchases: 'アイテム購入',
    trendChart: 'トレンドチャート',
    topScreens: 'Top スクリーン',
  },
  funnels: {
    title: 'ファネル分析',
    description: 'コンバージョンファネルの分析',
    viewDetail: '詳細を見る',
    steps: 'ステップ',
    dimensions: 'ディメンション',
    funnelNames: {
      registration: '新規登録',
      purchase: 'アイテム購入',
      return: '返却',
      designation: 'スタイリスト指名',
      diagnosis: 'パーソナルカラー診断',
      option_change: 'オプション変更',
      plan_change: 'プラン変更',
    },
  },
  users: {
    title: 'ユーザー行動',
    description: '個別ユーザーのイベントタイムライン',
    searchPlaceholder: 'ユーザーIDで検索...',
    search: '検索',
    noResults: 'ユーザーが見つかりません',
    sessions: 'セッション',
    events: 'イベント',
    lastActive: '最終アクティブ',
    dwellTime: '滞在時間',
    properties: 'プロパティ',
    userStatus: 'ユーザーステータス',
    noSearch: 'ユーザーIDを入力して検索してください',
    userId: 'ユーザーID',
    internalId: '内部ID',
    visitorId: 'ビジターID',
    eventNames: jaUserEventNames,
    propNames: jaUserPropNames,
  },
  errors: {
    title: 'エラー分析',
    description: 'エラー発生状況とエラー種別の内訳',
    totalErrors: '合計エラー数',
    avgErrorRate: '平均エラー率',
    sessionsAffected: '影響セッション数',
    dailyTrend: '日次エラー率推移',
    errorRate: 'エラー率',
    byType: 'エラー種別',
    byScreen: '画面別エラー',
    screen: '画面',
    event: 'イベント',
    count: '件数',
    uniqueSessions: 'セッション数',
    topMessages: 'エラーメッセージ TOP 30',
    message: 'エラーメッセージ',
    byAuthStatus: 'ログイン状態別エラー',
    loggedIn: 'ログインユーザー',
    anonymous: '未ログインユーザー',
  },
  theme: {
    light: 'ライトモード',
    dark: 'ダークモード',
  },
  login: {
    title: 'UX Mirror',
    subtitle: 'UX Analytics Dashboard',
    loginWith: 'Googleでログイン',
  },
};

/** @graph-connects none */
const en = {
  common: {
    noData: 'No data available',
    loading: 'Loading...',
    error: 'An error occurred',
    comingSoon: 'Coming Soon',
    comingSoonDescription: 'This feature is currently under development',
    last7days: '7 days',
    last30days: '30 days',
    last90days: '90 days',
  },
  nav: {
    overview: 'Overview',
    screens: 'Screen Analysis',
    funnels: 'Funnel Analysis',
    serviceHealth: 'Service Health',
    users: 'User Explorer',
    errors: 'Error Analysis',
    stylingOverview: 'Overview',
    stylingTiming: 'Timing Analysis',
    stylingErrors: 'Error Analysis',
    stylingSkuAnalysis: 'SKU Analysis',
    stylingFilterPatterns: 'Filter Patterns',
    stylingStylistActivity: 'Stylist Activity',
  },
  screens: {
    title: 'Screen Analysis',
    description: 'Metrics and user behavior by screen',
    search: 'Search screens...',
    screenName: 'Screen',
    totalEvents: 'Events',
    uniqueUsers: 'Unique Users',
    uniqueSessions: 'Unique Sessions',
    avgDwellTime: 'Avg Dwell Time',
    viewDetail: 'View Detail',
    backToList: 'Back to list',
    detailTitle: 'Screen Detail Analysis',
    metricsTab: 'Metrics',
    eventsTab: 'Events',
    dimensionsTab: 'Dimensions',
    dailyTrend: 'Daily Trend',
    eventBreakdown: 'Event Breakdown',
    dimensionBreakdown: 'Dimension Breakdown',
    eventCount: 'Event Count',
  },
  overview: {
    title: 'Overview',
    description: 'UX Insights overview',
    todayEvents: 'Today Events',
    todayUsers: 'Unique Users',
    todaySessions: 'Unique Sessions',
    avgDwellTime: 'Avg Dwell Time',
    trendChart: 'Trend Chart',
    topScreens: 'Top Screens',
  },
  funnels: {
    title: 'Funnel Analysis',
    description: 'Conversion funnel analysis',
    viewDetail: 'View Detail',
    steps: 'Steps',
    dimensions: 'Dimensions',
    funnelNames: {
      registration: 'Registration',
      purchase: 'Purchase',
      return: 'Return',
      designation: 'Designation',
      diagnosis: 'Diagnosis',
      option_change: 'Option Change',
      plan_change: 'Plan Change',
    },
  },
  users: {
    title: 'User Explorer',
    description: 'Individual user event timeline',
    searchPlaceholder: 'Search by user ID...',
    search: 'Search',
    noResults: 'User not found',
    sessions: 'Sessions',
    events: 'Events',
    lastActive: 'Last Active',
    dwellTime: 'Dwell Time',
    properties: 'Properties',
    userStatus: 'User Status',
    noSearch: 'Enter a user ID to search',
    userId: 'User ID',
    internalId: 'Internal ID',
    visitorId: 'Visitor ID',
    eventNames: enUserEventNames,
    propNames: enUserPropNames,
  },
  errors: {
    title: 'Error Analysis',
    description: 'Error occurrence and breakdown by type',
    totalErrors: 'Total Errors',
    avgErrorRate: 'Avg Error Rate',
    sessionsAffected: 'Sessions Affected',
    dailyTrend: 'Daily Error Rate Trend',
    errorRate: 'Error Rate',
    byType: 'Error Types',
    byScreen: 'Errors by Screen',
    screen: 'Screen',
    event: 'Event',
    count: 'Count',
    uniqueSessions: 'Sessions',
    topMessages: 'Top 30 Error Messages',
    message: 'Error Message',
    byAuthStatus: 'Errors by Login Status',
    loggedIn: 'Logged In',
    anonymous: 'Anonymous',
  },
  theme: {
    light: 'Light',
    dark: 'Dark',
  },
  login: {
    title: 'UX Mirror',
    subtitle: 'UX Analytics Dashboard',
    loginWith: 'Login with Google',
  },
};

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { ja: { translation: ja }, en: { translation: en } },
    fallbackLng: 'ja',
    interpolation: { escapeValue: false },
  });

/** @graph-connects none */
export default i18n;
